/**
 * Part planning for resumable uploads.
 *
 * Pure arithmetic, deliberately separated from the network code: an off-by-one
 * here does not throw, it assembles a corrupt video on the server, and that is
 * only catchable by testing the maths directly.
 *
 * Part numbers are 1-based to match the S3 multipart convention that MinIO
 * implements and that `uploadPartStateSchema` in @streamr/shared encodes
 * (`partNumber: min(1).max(10_000)`).
 */

export interface PartPlan {
  readonly partNumber: number;
  readonly start: number;
  /** Exclusive, so it can be handed straight to Blob.slice. */
  readonly end: number;
  readonly size: number;
}

export const planParts = (totalBytes: number, partSizeBytes: number): PartPlan[] => {
  if (!Number.isFinite(totalBytes) || totalBytes <= 0) return [];
  if (!Number.isFinite(partSizeBytes) || partSizeBytes <= 0) {
    throw new RangeError('partSizeBytes must be a positive number');
  }

  const parts: PartPlan[] = [];
  let start = 0;
  let partNumber = 1;
  while (start < totalBytes) {
    const end = Math.min(start + partSizeBytes, totalBytes);
    parts.push({ partNumber, start, end, size: end - start });
    start = end;
    partNumber += 1;
  }
  return parts;
};

/**
 * The parts still to send. The server is the authority on what it holds -- the
 * client never assumes a part landed just because it sent it, because the whole
 * point of resuming is that the last thing the client believed may be wrong.
 */
export const remainingParts = (plan: readonly PartPlan[], receivedPartNumbers: readonly number[]): PartPlan[] => {
  const received = new Set(receivedPartNumbers);
  return plan.filter((part) => !received.has(part.partNumber));
};

export const uploadedBytes = (plan: readonly PartPlan[], receivedPartNumbers: readonly number[]): number => {
  const received = new Set(receivedPartNumbers);
  return plan.reduce((sum, part) => (received.has(part.partNumber) ? sum + part.size : sum), 0);
};

/** 0..1, safe when the plan is empty. */
export const progressRatio = (plan: readonly PartPlan[], receivedPartNumbers: readonly number[]): number => {
  const total = plan.reduce((sum, part) => sum + part.size, 0);
  if (total === 0) return 0;
  return Math.min(1, uploadedBytes(plan, receivedPartNumbers) / total);
};

/**
 * Identifies a local file well enough to match it to a stored upload id after a
 * reload, without reading its contents.
 *
 * Name, size and mtime is not a content hash and cannot prove the bytes are the
 * same file. It does not need to: the consequence of a false match is a resumed
 * upload whose remaining parts come from a different file, so the resume path
 * ALSO checks the byte length the server recorded against the file it is
 * offered, and refuses the resume rather than silently splicing two files
 * together. Hashing a 5GB file in the browser to avoid re-uploading is the
 * wrong trade.
 */
export const fileFingerprint = (file: { name: string; size: number; lastModified: number }): string =>
  `${file.name}:${file.size}:${file.lastModified}`;
